import React, { useState, useEffect, useCallback } from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { Button as BsButton, Card, Col, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { FaEdit } from 'react-icons/fa';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import {
  useGetUsersQuery,
  useUpdateUserMutation,
} from '../../slices/usersApiSlice';
import { useGetAllLoginAuditsQuery } from '../../slices/userLoginAuditApiSlice';
import { toast } from 'react-toastify';

import { DataGrid, GridToolbar, gridClasses } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import {
  Collapse, IconButton, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Paper, Button, Tooltip,
} from '@mui/material';
import KeyboardArrowDownIcon  from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon    from '@mui/icons-material/KeyboardArrowUp';
import PersonOffIcon          from '@mui/icons-material/PersonOff';
import EditIcon               from '@mui/icons-material/Edit';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon     from '@mui/icons-material/CancelOutlined';
import PeopleIcon             from '@mui/icons-material/People';
import { useNavigate } from 'react-router-dom';
import { useLayout } from '../../context/LayoutContext';

// ─── Palette ──────────────────────────────────────────────────────────────────
const NAVY  = '#0d1b2a';
const TEAL  = '#0a8577';
const TEAL_L= '#0cb89e';
const SLATE = '#1e2d3d';
const MUTED = '#8fa3b1';
const WHITE = '#f0f4f8';
const GREEN = '#27ae60';
const RED_C = '#c0392b';
const AMBER = '#e8a020';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// ─── Styled DataGrid — defined OUTSIDE the component to avoid re-render loops ─
const StyledDataGrid = styled(DataGrid)({
  border: `1px solid ${TEAL}33`,
  borderRadius: 8,
  backgroundColor: SLATE,
  color: WHITE,
  fontFamily: '"DM Mono", "Courier New", monospace',
  fontSize: '0.78rem',

  // Header  '& .MuiDataGrid-row.inactiveRow': { backgroundColor: `${RED_C}11`, opacity: 0.7 }, 
  //    '& .MuiDataGrid-row.inactiveRow': { backgroundColor: '#2a2a2a', opacity: 0.65 },  SLATE


  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: NAVY,
    borderBottom: `1px solid ${TEAL}44`,
  },
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: NAVY,
    color: MUTED,
    fontFamily: '"DM Mono", monospace',
    fontSize: '0.68rem',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },
  '& .MuiDataGrid-columnSeparator': { color: `${TEAL}33` },
  '& .MuiDataGrid-sortIcon':        { color: TEAL_L },
  '& .MuiDataGrid-menuIconButton':  { color: MUTED },

  // Rows
  '& .MuiDataGrid-row': {
    backgroundColor: SLATE,
    '&:hover': { backgroundColor: `${TEAL}11` },
    '&.Mui-selected':       { backgroundColor: `${TEAL}22` },
    '&.Mui-selected:hover': { backgroundColor: `${TEAL}33` },
  },
  '& .MuiDataGrid-row.activeRow':   { backgroundColor: '#1c4849'},  
 
  '& .MuiDataGrid-row.inactiveRow': { backgroundColor: '#3a3f47', opacity: 0.7 },

  '& .MuiDataGrid-row.adminRow':    { borderLeft: `3px solid ${AMBER}` },

  // Cells
  '& .MuiDataGrid-cell': {
    borderColor: `${TEAL}11`,
    color: WHITE,
    fontFamily: '"DM Mono", monospace',
    alignItems: 'flex-start',
    paddingTop: 8,
    paddingBottom: 8,
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: `1px solid ${TEAL}`,
  },

  // Footer / pagination
  '& .MuiDataGrid-footerContainer': {
    backgroundColor: NAVY,
    borderTop: `1px solid ${TEAL}33`,
    color: MUTED,
  },
  '& .MuiTablePagination-root':    { color: MUTED },
  '& .MuiTablePagination-selectIcon': { color: MUTED },
  '& .MuiIconButton-root':         { color: MUTED },
  '& .MuiIconButton-root.Mui-disabled': { color: `${MUTED}44` },

  // Toolbar
  '& .MuiDataGrid-toolbarContainer': {
    backgroundColor: NAVY,
    borderBottom: `1px solid ${TEAL}22`,
    padding: '8px 12px',
    '& .MuiButton-root': { color: TEAL_L, fontFamily: '"DM Mono", monospace', fontSize: '0.72rem' },
    '& .MuiInputBase-root': { color: WHITE, fontFamily: '"DM Mono", monospace' },
    '& .MuiInputBase-root::before': { borderColor: `${TEAL}44` },
    '& .MuiSvgIcon-root': { color: MUTED },
  },

  // Checkbox
  '& .MuiCheckbox-root': { color: `${TEAL}88` },
  '& .MuiCheckbox-root.Mui-checked': { color: TEAL_L },

  // Cell class overrides
  '& .highBalance':    { color: GREEN,  fontWeight: 700 },
  '& .negativeBalance':{ color: RED_C,  fontWeight: 700 },
});

// ─── LoginHistoryPanel ────────────────────────────────────────────────────────
const LoginHistoryPanel = ({ userId, loginAudits }) => {
  const [open, setOpen] = useState(false);
  const logs = loginAudits?.[String(userId)] || [];

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          cursor: 'pointer', userSelect: 'none',
          color: logs.length > 0 ? TEAL_L : MUTED,
          fontSize: '0.72rem', fontFamily: '"DM Mono", monospace',
          '&:hover': { color: WHITE },
        }}
      >
        <IconButton size="small" sx={{ color: 'inherit', p: 0.25 }}>
          {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
        </IconButton>
        {logs.length > 0
          ? `${logs.length} event${logs.length !== 1 ? 's' : ''} recorded`
          : 'No history'}
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Paper elevation={0} sx={{
          mt: 0.5, bgcolor: NAVY,
          border: `1px solid ${TEAL}33`, borderRadius: 1, overflow: 'hidden',
        }}>
          {logs.length === 0 ? (
            <Box sx={{ p: 1.5 }}>
              <Typography sx={{ color: MUTED, fontSize: '0.72rem', fontFamily: '"DM Mono",monospace' }}>
                No history found.
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Time', 'Event', 'IP Address', 'Browser / Device', 'Note'].map(h => (
                    <TableCell key={h} sx={{
                      bgcolor: SLATE, color: MUTED,
                      fontFamily: '"DM Mono",monospace',
                      fontSize: '0.62rem', textTransform: 'uppercase',
                      letterSpacing: 1, borderColor: `${TEAL}22`,
                      whiteSpace: 'nowrap', py: 0.5,
                    }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log, idx) => {
                  const et = log.eventType || 'login';
                  const eventConfig = {
                    login:         { label: 'Login',       color: GREEN },
                    logout_manual: { label: 'Logout',      color: TEAL_L },
                    logout_auto:   { label: 'Auto Logout', color: AMBER },
                  }[et] || { label: et, color: MUTED };
                  const chipColor  = log.success === false ? RED_C : eventConfig.color;
                  const chipLabel  = log.success === false ? 'Failed Login' : eventConfig.label;

                  return (
                    <TableRow key={log._id || idx} sx={{
                      '&:last-child td': { border: 0 },
                      bgcolor: idx === 0 ? `${TEAL}0a` : 'transparent',
                      '&:hover': { bgcolor: `${TEAL}11` },
                    }}>
                      <TableCell sx={{
                        color: idx === 0 ? TEAL_L : WHITE,
                        fontFamily: '"DM Mono",monospace', fontSize: '0.7rem',
                        borderColor: `${TEAL}11`, whiteSpace: 'nowrap',
                      }}>
                        {fmtDate(log.loginTime)}
                        {idx === 0 && (
                          <Chip label="Latest" size="small" sx={{
                            ml: 0.75, height: 14, fontSize: '0.58rem',
                            bgcolor: `${TEAL}33`, color: TEAL_L,
                            border: `1px solid ${TEAL}44`,
                          }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ borderColor: `${TEAL}11` }}>
                        <Chip label={chipLabel} size="small" sx={{
                          height: 16, fontSize: '0.62rem', fontWeight: 700,
                          bgcolor: `${chipColor}22`, color: chipColor,
                          border: `1px solid ${chipColor}44`,
                        }} />
                      </TableCell>
                      <TableCell sx={{
                        color: MUTED, fontFamily: '"DM Mono",monospace',
                        fontSize: '0.68rem', borderColor: `${TEAL}11`,
                      }}>
                        {log.ipAddress || '—'}
                      </TableCell>
                      <TableCell sx={{
                        color: MUTED, fontFamily: '"DM Mono",monospace',
                        fontSize: '0.65rem', borderColor: `${TEAL}11`,
                        maxWidth: 240, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        <span
                          data-tooltip-id={`ua-${log._id}`}
                          data-tooltip-content={log.userAgent || ''}
                          style={{ cursor: 'default' }}
                        >
                          {log.userAgent
                            ? log.userAgent.length > 50
                              ? log.userAgent.substring(0, 48) + '…'
                              : log.userAgent
                            : '—'}
                        </span>
                        <ReactTooltip id={`ua-${log._id}`} place="top" />
                      </TableCell>
                      <TableCell sx={{
                        color: AMBER, fontFamily: '"DM Mono",monospace',
                        fontSize: '0.68rem', borderColor: `${TEAL}11`,
                      }}>
                        {log.note || '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Collapse>
    </Box>
  );
};

// ─── Main Screen ──────────────────────────────────────────────────────────────
const UserListScreen = () => {
  const { data: users = [], refetch, isLoading, error } = useGetUsersQuery();
  const { data: loginAudits = {} } = useGetAllLoginAuditsQuery();
  const [updateUser] = useUpdateUserMutation();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const { userInfo } = useSelector((state) => state.auth);
  const { setFluid } = useLayout();
  const navigate = useNavigate();

  useEffect(() => {
    setFluid(true);
    return () => setFluid(false);
  }, [setFluid]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  let filteredUsers = [];
  if (users && Array.isArray(users)) {
    const usersCopy = [...users];
    filteredUsers = (!userInfo.isRadAdmin
      ? usersCopy.filter(u => !u.isRadAdmin)
      : usersCopy
    ).sort((a, b) => a.name.localeCompare(b.name));
  }

  // ── Soft-delete: sets isActive = false ────────────────────────────────────
  const deactivateHandler = async (id, name) => {
    if (window.confirm(`Deactivate user "${name}"? They will no longer be able to log in.`)) {
      try {
        await updateUser({ userId: id, isActive: false }).unwrap();
        toast.success(`${name} has been deactivated`);
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error || 'Failed to deactivate user');
      }
    }
  };

  const renderErrorOrLoading = () => {
    if (isLoading) return <Loader />;
    if (error) return <Message variant="danger">{error?.data?.message || error.error}</Message>;
    return null;
  };

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      width: 170,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ color: WHITE, fontFamily: '"DM Mono",monospace', fontSize: '0.78rem', fontWeight: 300 }}>
            {params.value}
          </Typography>
          {params.row.speciality && (
            <Typography sx={{ color: MUTED, fontFamily: '"DM Mono",monospace', fontSize: '0.65rem' }}>
              {params.row.speciality}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 220,
      renderCell: (params) => (
        <Typography sx={{ color: TEAL_L, fontFamily: '"DM Mono",monospace', fontSize: '0.72rem' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'annualLeave',
      headerName: 'Annual Leave',
      width: 130,
      type: 'number',
      renderCell: (params) => (
        <Box
          data-tooltip-id="leave-tooltip"
          data-tooltip-html={
            `Opening Balance: ${params.row.openingBalance?.toFixed(2) || '0.00'} hrs<br/>` +
            `Bring Forward: ${params.row.bringForward?.toFixed(2) || '0.00'} hrs`
          }
        >
          <Typography sx={{
            fontFamily: '"DM Mono",monospace', fontSize: '0.78rem',
            color: params.value > 200 ? GREEN : params.value < 0 ? RED_C : WHITE,
            fontWeight: (params.value > 200 || params.value < 0) ? 500 : 400,
          }}>
            {(params.value || 0).toFixed(2)} hrs
          </Typography>
        </Box>
      ),
    },
    {
      field: 'isAdmin',
      headerName: 'Admin',
      width: 75,
      renderCell: (params) => params.value
        ? <CheckCircleOutlineIcon sx={{ color: AMBER, fontSize: 18 }} />
        : <CancelOutlinedIcon    sx={{ color: `${MUTED}55`, fontSize: 18 }} />,
    },
    {
      field: 'areyouaclinicaldirector',
      headerName: 'Manager',
      width: 85,
      renderCell: (params) => params.value
        ? <CheckCircleOutlineIcon sx={{ color: TEAL_L, fontSize: 18 }} />
        : <CancelOutlinedIcon    sx={{ color: `${MUTED}55`, fontSize: 18 }} />,
    },
    {
      field: 'isApproved',
      headerName: 'Approved',
      width: 90,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Pending'}
          size="small"
          sx={{
            height: 18, fontSize: '0.62rem', fontWeight: 700,
            bgcolor: params.value ? `${GREEN}22` : `${AMBER}22`,
            color:   params.value ? GREEN : AMBER,
            border:  `1px solid ${params.value ? GREEN : AMBER}44`,
          }}
        />
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 90,
      renderCell: (params) => (
        <Chip
          label={params.value !== false ? 'Active' : 'Inactive'}
          size="small"
          sx={{
            height: 18, fontSize: '0.62rem', fontWeight: 700,
            bgcolor: params.value !== false ? `${TEAL}22` : `${RED_C}22`,
            color:   params.value !== false ? TEAL_L : RED_C,
            border:  `1px solid ${params.value !== false ? TEAL : RED_C}44`,
          }}
        />
      ),
    },
    {
      field: 'loginHistory',
      headerName: 'Login History',
      width: 300,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <LoginHistoryPanel userId={params.row.id} loginAudits={loginAudits} />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Edit user">
            <Button
              size="small"
              onClick={() => navigate(`/admin/user/${params.row.id}/edit`)}
              sx={{
                minWidth: 0, px: 1.5, py: 0.5,
                bgcolor: `${TEAL}22`, color: TEAL_L,
                border: `1px solid ${TEAL}44`,
                fontFamily: '"DM Mono",monospace', fontSize: '0.7rem',
                '&:hover': { bgcolor: TEAL, color: WHITE },
              }}
            >
              <EditIcon sx={{ fontSize: 14, mr: 0.5 }} />
              Edit
            </Button>
          </Tooltip>

          <Tooltip title={params.row.isActive !== false ? 'Deactivate user (sets inactive)' : 'User already inactive'}>
            <span>
              <Button
                size="small"
                disabled={params.row.isActive === false}
                onClick={() => deactivateHandler(params.row.id, params.row.name)}
                sx={{
                  minWidth: 0, px: 1.5, py: 0.5,
                  bgcolor: params.row.isActive === false ? `${MUTED}11` : `${RED_C}22`,
                  color:   params.row.isActive === false ? MUTED : RED_C,
                  border:  `1px solid ${params.row.isActive === false ? MUTED : RED_C}44`,
                  fontFamily: '"DM Mono",monospace', fontSize: '0.7rem',
                  '&:hover': { bgcolor: RED_C, color: WHITE },
                  '&.Mui-disabled': { color: `${MUTED}44`, border: `1px solid ${MUTED}22` },
                }}
              >
                <PersonOffIcon sx={{ fontSize: 14, mr: 0.5 }} />
                {params.row.isActive === false ? 'Inactive' : 'Deactivate'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // ── Rows ──────────────────────────────────────────────────────────────────
  const rows = filteredUsers.map((user) => ({
    id:                      user._id,
    speciality:              user.speciality || '',
    name:                    user.name,
    email:                   user.email,
    openingBalance:          user.openingBalance || 0,
    bringForward:            user.bringForward   || 0,
    annualLeave:             (user.openingBalance || 0) + (user.bringForward || 0),
    isAdmin:                 user.isAdmin,
    areyouaclinicaldirector: user.areyouaclinicaldirector,
    isApproved:              user.isApproved,
    isActive:                user.isActive,
    isLocum:                 user.isLocum,
    locumEndDate:            user.locumEndDate,
  }));

  const getRowSpacing = React.useCallback((params) => ({
    top:    params.isFirstVisible ? 0 : 1,
    bottom: params.isLastVisible  ? 0 : 1,
  }), []);

  const getRowClassName = (params) => {
    if (params.row.isActive === false) return 'inactiveRow';
    if (params.row.isAdmin)            return 'adminRow';
    return 'activeRow';
  };

  // ── Stats bar ─────────────────────────────────────────────────────────────
  const totalUsers    = filteredUsers.length;
  const activeUsers   = filteredUsers.filter(u => u.isActive !== false).length;
  const inactiveUsers = filteredUsers.filter(u => u.isActive === false).length;
  const pendingUsers  = filteredUsers.filter(u => !u.isApproved).length;
  const adminUsers    = filteredUsers.filter(u => u.isAdmin).length;

  // ── Mobile card view ──────────────────────────────────────────────────────
  const renderUsersCards = () => (
    <Box sx={{ p: 2 }}>
      {filteredUsers.map((user) => {
        const annualLeave = (user.openingBalance || 0) + (user.bringForward || 0);
        const logs = loginAudits?.[String(user._id)] || [];
        const isInactive = user.isActive === false;

        return (
          <Paper key={user._id} elevation={0} sx={{
            mb: 2, bgcolor: isInactive ? `${RED_C}0a` : SLATE,
            border: `1px solid ${isInactive ? RED_C : TEAL}33`, borderRadius: 2, p: 2,
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box>
                <Typography sx={{ color: WHITE, fontFamily: '"DM Mono",monospace', fontWeight: 700 }}>
                  {user.name}
                </Typography>
                <Typography sx={{ color: TEAL_L, fontFamily: '"DM Mono",monospace', fontSize: '0.75rem' }}>
                  {user.email}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {user.isAdmin && <Chip label="Admin" size="small" sx={{ bgcolor: `${AMBER}22`, color: AMBER, fontSize: '0.6rem', height: 18 }} />}
                <Chip label={isInactive ? 'Inactive' : 'Active'} size="small"
                  sx={{ bgcolor: isInactive ? `${RED_C}22` : `${GREEN}22`, color: isInactive ? RED_C : GREEN, fontSize: '0.6rem', height: 18 }} />
              </Box>
            </Box>

            <Typography sx={{ color: MUTED, fontFamily: '"DM Mono",monospace', fontSize: '0.72rem', mb: 1 }}>
              Annual Leave: {annualLeave.toFixed(2)} hrs
            </Typography>

            {logs.length > 0 && (
              <Typography sx={{ color: MUTED, fontFamily: '"DM Mono",monospace', fontSize: '0.7rem', mb: 1 }}>
                Last seen: {fmtDate(logs[0].loginTime)}
                {' '}
                <Chip label={logs[0].eventType || 'login'} size="small" sx={{ height: 14, fontSize: '0.58rem', bgcolor: `${TEAL}22`, color: TEAL_L }} />
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button size="small" onClick={() => navigate(`/admin/user/${user._id}/edit`)}
                sx={{ bgcolor: `${TEAL}22`, color: TEAL_L, border: `1px solid ${TEAL}44`, fontSize: '0.7rem', fontFamily: '"DM Mono",monospace', '&:hover': { bgcolor: TEAL, color: WHITE } }}>
                <EditIcon sx={{ fontSize: 13, mr: 0.5 }} /> Edit
              </Button>
              {!isInactive && (
                <Button size="small" onClick={() => deactivateHandler(user._id, user.name)}
                  sx={{ bgcolor: `${RED_C}22`, color: RED_C, border: `1px solid ${RED_C}44`, fontSize: '0.7rem', fontFamily: '"DM Mono",monospace', '&:hover': { bgcolor: RED_C, color: WHITE } }}>
                  <PersonOffIcon sx={{ fontSize: 13, mr: 0.5 }} /> Deactivate
                </Button>
              )}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );

  // ── Desktop table ─────────────────────────────────────────────────────────
  const renderUsersTable = () => (
    <Box sx={{ height: 800, width: '100%', overflowX: 'auto' }}>
      <StyledDataGrid
        density="compact"
        rows={rows}
        columns={columns}
        getRowClassName={getRowClassName}
        getRowSpacing={getRowSpacing}
        getRowHeight={() => 'auto'}
        sx={{ minWidth: 1440 }}
        slots={{ toolbar: GridToolbar }}
        slotProps={{ toolbar: { showQuickFilter: true } }}
        pageSizeOptions={[25, 50, 100]}
        checkboxSelection
        disableRowSelectionOnClick
      />
    </Box>
  );

  return (
    <Box sx={{
      minHeight: '100vh', bgcolor: NAVY, p: { xs: 2, md: 3 },
      fontFamily: '"DM Mono","Courier New",monospace',
    }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500;700&display=swap');`}</style>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <PeopleIcon sx={{ color: TEAL, fontSize: 28 }} />
            <Typography sx={{ fontFamily: '"DM Mono",monospace', fontSize: { xs: '1.2rem', md: '1.6rem' }, fontWeight: 700, color: WHITE, letterSpacing: 1 }}>
              User Management
            </Typography>
          </Box>
          <Typography sx={{ color: MUTED, fontSize: '0.72rem', fontFamily: '"DM Mono",monospace', letterSpacing: 1.5 }}>
            BEAUMONT HOSPITAL · ADMIN PANEL
          </Typography>
        </Box>

        {/* Stats chips */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {[
            { label: `${totalUsers} total`,    color: TEAL },
            { label: `${activeUsers} active`,  color: GREEN },
            { label: `${inactiveUsers} inactive`, color: RED_C },
            { label: `${pendingUsers} pending`,color: AMBER },
            { label: `${adminUsers} admins`,   color: AMBER },
          ].map(({ label, color }) => (
            <Chip key={label} label={label} size="small" sx={{
              bgcolor: `${color}22`, color, fontFamily: '"DM Mono",monospace',
              fontSize: '0.7rem', border: `1px solid ${color}44`,
            }} />
          ))}
        </Box>
      </Box>

      {/* ── Legend ────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {[
          { label: 'Active user',   color: SLATE,          border: TEAL },
          { label: 'Admin user',    color: SLATE,          border: AMBER, leftBorder: true },
          { label: 'Inactive user', color: `${RED_C}11`,   border: RED_C },
        ].map(({ label, color, border, leftBorder }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{
              width: 16, height: 16, borderRadius: 0.5, bgcolor: color,
              border: `1px solid ${border}44`,
              borderLeft: leftBorder ? `3px solid ${border}` : undefined,
            }} />
            <Typography sx={{ color: MUTED, fontSize: '0.68rem', fontFamily: '"DM Mono",monospace' }}>{label}</Typography>
          </Box>
        ))}
        <Typography sx={{ color: MUTED, fontSize: '0.68rem', fontFamily: '"DM Mono",monospace', ml: 1 }}>
          · Deactivate sets the user as inactive — no database deletion
        </Typography>
      </Box>

      {renderErrorOrLoading()}

      {windowWidth < 900 ? renderUsersCards() : renderUsersTable()}

      <ReactTooltip id="leave-tooltip" place="top" />
    </Box>
  );
};

export default UserListScreen;
